import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { VehicleTechnicalDataPanel } from '@/components/vehicles/technical-data';
import {
  HAYNESPRO_CAR_TYPE_GROUPS,
  isAbortError,
  isStaleWorkOrderTimeError,
  parseWorkOrderTimeError,
  useHaynesProWorkOrderTime,
  type HaynesProAdoptResult,
  type HaynesProBrowseResult,
  type HaynesProCarTypeGroup,
  type HaynesProPreviewResult,
  type HaynesProRepairTreeNode,
  type WorkOrderTimeErrorCode,
} from '@/hooks/useHaynesProWorkOrderTime';
import { useVehicleTechnicalData } from '@/hooks/useVehicleTechnicalData';
import { useVehicle } from '@/hooks/useVehicles';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import {
  DEFAULT_CAR_TYPE_GROUP,
  canEnterGroup,
  formatHaynesProHours,
  formatHaynesProMinutes,
  hasAuthoritativeGuideTime,
  isHaynesProOverlapBlocked,
  isMappedToOtherLaborItem,
  isSelectableLeaf,
  needsConfirmedVariant,
  previewPathForLeaf,
} from './haynesproWorkOrderTime';

export interface HaynesProWorkOrderTimeDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
  vehicleId: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  bookingServiceId: string;
  laborItemId: string;
  currentHours: number;
  isManualOverride: boolean;
  pricingType?: string | null;
  onAdopted: (result: HaynesProAdoptResult) => void;
}

interface Ancestor {
  id: string;
  description: string;
}

function errorMessageKey(code: WorkOrderTimeErrorCode | 'unknown'): string {
  return `detail.haynesProTime.errors.${code}`;
}

function nodeLabel(node: Pick<HaynesProRepairTreeNode, 'description' | 'id'>): string {
  return node.description?.trim() || node.id;
}

export function HaynesProWorkOrderTimeDialog({
  open,
  onClose,
  workOrderId,
  vehicleId,
  vehicleMake,
  vehicleModel,
  bookingServiceId,
  laborItemId,
  currentHours,
  isManualOverride,
  pricingType,
  onAdopted,
}: HaynesProWorkOrderTimeDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canUpdate = useAuthStore((state) => state.hasPermission('work_orders.update'));
  const { capabilityQuery, browseMutation, previewMutation, adoptMutation } =
    useHaynesProWorkOrderTime(workOrderId);
  const vehicleTechnical = useVehicleTechnicalData(open ? vehicleId : undefined);
  const vehicleQuery = useVehicle(open ? vehicleId : undefined);

  const browseGenerationRef = useRef(0);
  const previewGenerationRef = useRef(0);
  const adoptGenerationRef = useRef(0);
  const workOrderIdRef = useRef(workOrderId);
  const laborItemIdRef = useRef(laborItemId);
  const vehicleIdRef = useRef(vehicleId);
  workOrderIdRef.current = workOrderId;
  laborItemIdRef.current = laborItemId;
  vehicleIdRef.current = vehicleId;

  const [carTypeGroup, setCarTypeGroup] = useState<HaynesProCarTypeGroup>(DEFAULT_CAR_TYPE_GROUP);
  const [ancestors, setAncestors] = useState<Ancestor[]>([]);
  const [nodes, setNodes] = useState<readonly HaynesProRepairTreeNode[]>([]);
  const [browsePending, setBrowsePending] = useState(false);
  const [browseError, setBrowseError] = useState<WorkOrderTimeErrorCode | 'unknown' | null>(null);
  const [selectedLeaf, setSelectedLeaf] = useState<HaynesProRepairTreeNode | null>(null);
  const [preview, setPreview] = useState<HaynesProPreviewResult | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [previewError, setPreviewError] = useState<WorkOrderTimeErrorCode | 'unknown' | null>(null);
  const [replaceManualOverride, setReplaceManualOverride] = useState(false);
  const [adoptPending, setAdoptPending] = useState(false);
  const [adoptError, setAdoptError] = useState<WorkOrderTimeErrorCode | 'unknown' | null>(null);
  const [staleNotice, setStaleNotice] = useState(false);

  const resetPicker = () => {
    browseGenerationRef.current += 1;
    previewGenerationRef.current += 1;
    adoptGenerationRef.current += 1;
    setCarTypeGroup(DEFAULT_CAR_TYPE_GROUP);
    setAncestors([]);
    setNodes([]);
    setBrowsePending(false);
    setBrowseError(null);
    setSelectedLeaf(null);
    setPreview(null);
    setPreviewPending(false);
    setPreviewError(null);
    setReplaceManualOverride(false);
    setAdoptPending(false);
    setAdoptError(null);
    setStaleNotice(false);
  };

  useEffect(() => {
    if (!open) {
      resetPicker();
    }
    // Reset only when the dialog is closed so in-progress browse is not dropped.
  }, [open]);

  useEffect(() => {
    resetPicker();
    // Identity change always drops in-flight selection.
  }, [workOrderId, vehicleId, laborItemId]);

  const capability = capabilityQuery.data;
  const variantRequired = needsConfirmedVariant(capability);
  const usableVariant = capability?.confirmedVariant;

  useEffect(() => {
    if (!open) return;
    if (!vehicleTechnical.capabilityQuery.data?.confirmedVariant?.usable) return;
    void capabilityQuery.refetch();
    // Refetch work-order capability after the vehicle confirmation panel succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    vehicleTechnical.capabilityQuery.data?.confirmedVariant?.revision,
    vehicleTechnical.capabilityQuery.data?.confirmedVariant?.usable,
  ]);

  const handleStale = async () => {
    browseGenerationRef.current += 1;
    previewGenerationRef.current += 1;
    setAncestors([]);
    setNodes([]);
    setSelectedLeaf(null);
    setPreview(null);
    setBrowseError(null);
    setPreviewError(null);
    setAdoptError(null);
    setBrowsePending(false);
    setPreviewPending(false);
    setStaleNotice(true);
    await capabilityQuery.refetch();
    notify.warning(t('detail.haynesProTime.stale'), t('detail.haynesProTime.staleHint'));
  };

  const runBrowse = async (
    group: HaynesProCarTypeGroup,
    parent: Ancestor[],
    nodeId: string | undefined,
  ) => {
    if (!capability || variantRequired) return;
    if (!capability.currentSourceFingerprint) return;
    browseGenerationRef.current += 1;
    const generation = browseGenerationRef.current;
    setBrowsePending(true);
    setBrowseError(null);
    setSelectedLeaf(null);
    setPreview(null);
    setPreviewError(null);
    setAdoptError(null);
    try {
      const result: HaynesProBrowseResult = await browseMutation.mutateAsync({
        carTypeGroup: group,
        ...(nodeId ? { nodeId } : {}),
        expectedSourceFingerprint: capability.currentSourceFingerprint,
        expectedCurrentVariantRevision: capability.confirmedVariant?.revision ?? 0,
      });
      if (generation !== browseGenerationRef.current) return;
      if (workOrderIdRef.current !== workOrderId || laborItemIdRef.current !== laborItemId) return;
      setAncestors(parent);
      setNodes(result.nodes);
    } catch (error) {
      if (generation !== browseGenerationRef.current) return;
      if (isAbortError(error)) return;
      const parsed = parseWorkOrderTimeError(error);
      if (isStaleWorkOrderTimeError(parsed.code)) {
        await handleStale();
        return;
      }
      setBrowseError(parsed.code);
      setNodes([]);
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (generation === browseGenerationRef.current) {
        setBrowsePending(false);
      }
    }
  };

  const startAtRoot = (group: HaynesProCarTypeGroup) => {
    const rootId = usableVariant?.rootNodeId;
    if (!rootId) return;
    const root: Ancestor = { id: rootId, description: t('detail.haynesProTime.root') };
    void runBrowse(group, [root], undefined);
  };

  useEffect(() => {
    if (!open) return;
    if (!capability || variantRequired) return;
    if (ancestors.length > 0 || browsePending || browseError) return;
    startAtRoot(carTypeGroup);
    // Auto-browse the confirmed root after capability is usable. Never auto-select a leaf.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, capability, variantRequired, carTypeGroup]);

  const handleGroupChange = (
    _event: MouseEvent<HTMLElement>,
    next: HaynesProCarTypeGroup | null,
  ) => {
    if (!next || next === carTypeGroup) return;
    setCarTypeGroup(next);
    setAncestors([]);
    setNodes([]);
    setSelectedLeaf(null);
    setPreview(null);
    setBrowseError(null);
    setPreviewError(null);
  };

  const handleAncestorClick = (index: number) => {
    const next = ancestors.slice(0, index + 1);
    const parent = next[next.length - 1];
    if (!parent) return;
    const nodeId = index === 0 ? undefined : parent.id;
    void runBrowse(carTypeGroup, next, nodeId);
  };

  const runPreview = async (leaf: HaynesProRepairTreeNode, ancestorIds: readonly string[]) => {
    if (!capability || variantRequired) return;
    const path = previewPathForLeaf(ancestorIds, leaf.id);
    previewGenerationRef.current += 1;
    const generation = previewGenerationRef.current;
    setPreviewPending(true);
    setPreviewError(null);
    setPreview(null);
    setAdoptError(null);
    try {
      const result = await previewMutation.mutateAsync({
        bookingServiceId,
        laborItemId,
        carTypeGroup,
        path,
        expectedSourceFingerprint: capability.currentSourceFingerprint,
        expectedCurrentVariantRevision: capability.confirmedVariant?.revision ?? 0,
      });
      if (generation !== previewGenerationRef.current) return;
      if (laborItemIdRef.current !== laborItemId) return;
      setPreview(result);
    } catch (error) {
      if (generation !== previewGenerationRef.current) return;
      if (isAbortError(error)) return;
      const parsed = parseWorkOrderTimeError(error);
      if (isStaleWorkOrderTimeError(parsed.code)) {
        await handleStale();
        return;
      }
      setPreviewError(parsed.code);
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (generation === previewGenerationRef.current) {
        setPreviewPending(false);
      }
    }
  };

  const handleNodeClick = (node: HaynesProRepairTreeNode) => {
    if (browsePending || previewPending || adoptPending) return;
    if (node.hasSubnodes) {
      if (!canEnterGroup(ancestors.length, node)) return;
      const nextAncestors = [...ancestors, { id: node.id, description: nodeLabel(node) }];
      void runBrowse(carTypeGroup, nextAncestors, node.id);
      return;
    }
    if (!isSelectableLeaf(node)) {
      setSelectedLeaf(null);
      setPreview(null);
      setPreviewError(node.unsupportedReason ? null : 'leaf_required');
      return;
    }
    setSelectedLeaf(node);
    void runPreview(node, ancestors.map((item) => item.id));
  };

  const handleReload = () => {
    resetPicker();
    void capabilityQuery.refetch();
  };

  const handleAdopt = async () => {
    if (!preview || !canUpdate || adoptPending) return;
    if (!hasAuthoritativeGuideTime(preview)) return;
    if (isManualOverride && !replaceManualOverride) return;
    if (isHaynesProOverlapBlocked(capability?.activeSnapshot, preview.node.id, laborItemId)) {
      return;
    }
    const targetWorkOrderId = workOrderId;
    const targetLaborItemId = laborItemId;
    adoptGenerationRef.current += 1;
    const generation = adoptGenerationRef.current;
    setAdoptPending(true);
    setAdoptError(null);
    try {
      const { result } = await adoptMutation.mutateAsync({
        confirmed: true,
        previewToken: preview.previewToken,
        bookingServiceId,
        laborItemId: targetLaborItemId,
        ...(isManualOverride || replaceManualOverride
          ? { replaceManualOverride: true }
          : {}),
      });
      if (generation !== adoptGenerationRef.current) return;
      if (
        workOrderIdRef.current !== targetWorkOrderId ||
        laborItemIdRef.current !== targetLaborItemId
      ) {
        return;
      }
      onAdopted(result);
      notify.success(t('detail.haynesProTime.success'), t('detail.haynesProTime.successDesc'));
      onClose();
    } catch (error) {
      if (generation !== adoptGenerationRef.current) return;
      if (isAbortError(error)) return;
      const parsed = parseWorkOrderTimeError(error);
      if (isStaleWorkOrderTimeError(parsed.code)) {
        await handleStale();
        return;
      }
      if (parsed.code === 'preview_expired') {
        setPreview(null);
        setPreviewError(parsed.code);
      }
      if (parsed.code === 'manual_override') {
        setReplaceManualOverride(false);
      }
      setAdoptError(parsed.code);
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (generation === adoptGenerationRef.current) {
        setAdoptPending(false);
      }
    }
  };

  const mappedElsewhere = isMappedToOtherLaborItem(capability?.activeSnapshot, laborItemId);
  const overlapBlocked = isHaynesProOverlapBlocked(
    capability?.activeSnapshot,
    preview?.node.id ?? selectedLeaf?.id,
    laborItemId,
  );
  const guideReady = hasAuthoritativeGuideTime(preview);
  const needsManualConsent = isManualOverride || adoptError === 'manual_override';
  const canAdopt =
    canUpdate &&
    guideReady &&
    !overlapBlocked &&
    !adoptPending &&
    !previewPending &&
    !browsePending &&
    (!needsManualConsent || replaceManualOverride);

  const capabilityPending = capabilityQuery.isPending;
  const capabilityError = capabilityQuery.isError
    ? parseWorkOrderTimeError(capabilityQuery.error)
    : null;
  const snapshotsOff = capability != null && capability.snapshotsEnabled !== true;
  const entitlementOff =
    capability != null && capability.snapshotsEnabled === true && capability.available !== true;
  const hasVin = Boolean(vehicleQuery.data?.vin);

  return (
    <ResponsiveDialog
      open={open}
      onClose={adoptPending ? undefined : onClose}
      fullWidth
      maxWidth="md"
      mobileTitle={t('detail.haynesProTime.title')}
    >
      <DialogHeader
        title={t('detail.haynesProTime.title')}
        icon={<TimerOutlinedIcon />}
        onClose={adoptPending ? undefined : onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          {capabilityPending && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
              role="status"
              aria-label={t('detail.haynesProTime.pending')}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {capabilityError && (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button color="inherit" size="small" onClick={handleReload}>
                  {t('detail.haynesProTime.staleReload')}
                </Button>
              }
            >
              {t(errorMessageKey(capabilityError.code))}
            </Alert>
          )}

          {snapshotsOff && (
            <Alert severity="info" role="status">
              {t('detail.haynesProTime.snapshotsDisabled')}
            </Alert>
          )}

          {entitlementOff && (
            <Alert severity="info" role="status">
              {t('detail.haynesProTime.unavailable')}
            </Alert>
          )}

          {staleNotice && (
            <Alert
              severity="warning"
              role="status"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleReload}
                  aria-label={t('detail.haynesProTime.staleReload')}
                >
                  {t('detail.haynesProTime.staleReload')}
                </Button>
              }
            >
              {t('detail.haynesProTime.staleHint')}
            </Alert>
          )}

          {capability && capability.snapshotsEnabled && variantRequired && (
            <Stack spacing={2}>
              <Alert severity="info" role="status">
                {t('detail.haynesProTime.confirmVariantHint')}
              </Alert>
              <VehicleTechnicalDataPanel
                vehicleId={vehicleId}
                make={vehicleMake ?? vehicleQuery.data?.make}
                model={vehicleModel ?? vehicleQuery.data?.model}
                hasVin={hasVin}
              />
            </Stack>
          )}

          {capability && capability.snapshotsEnabled && capability.available && !variantRequired && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('detail.haynesProTime.selectLeaf')}
              </Typography>

              {pricingType === 'fixed' && (
                <Alert severity="info" role="status">
                  {t('detail.haynesProTime.fixedPriceHint')}
                </Alert>
              )}

              {capability.activeSnapshot && (
                <Alert severity="info" role="status">
                  {t('detail.haynesProTime.oneOperationHint')}
                </Alert>
              )}

              <ToggleButtonGroup
                exclusive
                size="small"
                value={carTypeGroup}
                onChange={handleGroupChange}
                aria-label={t('detail.haynesProTime.selectGroup')}
                sx={{ flexWrap: 'wrap' }}
              >
                {HAYNESPRO_CAR_TYPE_GROUPS.map((group) => (
                  <ToggleButton key={group} value={group} aria-label={t(`detail.haynesProTime.groups.${group}`)}>
                    {t(`detail.haynesProTime.groups.${group}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {ancestors.length > 0 && (
                <Breadcrumbs
                  separator={<ChevronRightIcon fontSize="small" />}
                  aria-label={t('detail.haynesProTime.breadcrumb')}
                >
                  {ancestors.map((item, index) => (
                    <Link
                      key={`${item.id}-${index}`}
                      component="button"
                      type="button"
                      underline="hover"
                      color="inherit"
                      onClick={() => handleAncestorClick(index)}
                    >
                      {item.description}
                    </Link>
                  ))}
                </Breadcrumbs>
              )}

              {browsePending && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  role="status"
                  aria-label={t('detail.haynesProTime.browsePending')}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t('detail.haynesProTime.browsePending')}</Typography>
                </Box>
              )}

              {browseError && (
                <Alert severity="error" role="alert">
                  {t(errorMessageKey(browseError))}
                </Alert>
              )}

              {!browsePending && !browseError && nodes.length === 0 && ancestors.length > 0 && (
                <Alert severity="info" role="status">
                  {t('detail.haynesProTime.emptyTree')}
                </Alert>
              )}

              {nodes.length > 0 && (
                <List
                  dense
                  disablePadding
                  aria-label={t('detail.haynesProTime.treeAria')}
                  sx={{ maxHeight: 280, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  {nodes.map((node) => {
                    const selected = selectedLeaf?.id === node.id;
                    const leaf = isSelectableLeaf(node);
                    const displayHours =
                      leaf && node.decimalHours != null
                        ? formatHaynesProHours(node.decimalHours)
                        : null;
                    const unsupportedKey = node.unsupportedReason
                      ? `detail.haynesProTime.unsupported.${node.unsupportedReason}`
                      : null;
                    const primaryLabel = node.oeCode
                      ? `${nodeLabel(node)} (${node.oeCode})`
                      : nodeLabel(node);
                    return (
                      <ListItemButton
                        key={node.id}
                        selected={selected}
                        onClick={() => handleNodeClick(node)}
                        disabled={browsePending || previewPending || adoptPending}
                        aria-label={
                          leaf
                            ? t('detail.haynesProTime.leafOption', { name: primaryLabel })
                            : t('detail.haynesProTime.groupOption', { name: primaryLabel })
                        }
                      >
                        <ListItemText
                          primary={primaryLabel}
                          secondary={
                            unsupportedKey
                              ? t(unsupportedKey)
                              : leaf && displayHours !== null && displayHours !== ''
                                ? t('detail.haynesProTime.treeTimeHint', { hours: displayHours })
                                : node.hasSubnodes
                                  ? t('detail.haynesProTime.groupHint')
                                  : t('detail.haynesProTime.noTime')
                          }
                        />
                        {node.hasSubnodes && <ChevronRightIcon fontSize="small" color="action" />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}

              {previewError === 'leaf_required' && (
                <Alert severity="info" role="status">
                  {t('detail.haynesProTime.leafRequired')}
                </Alert>
              )}

              {previewPending && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  role="status"
                  aria-label={t('detail.haynesProTime.previewPending')}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t('detail.haynesProTime.previewPending')}</Typography>
                </Box>
              )}

              {previewError && previewError !== 'leaf_required' && (
                <Alert severity="error" role="alert">
                  {t(errorMessageKey(previewError))}
                </Alert>
              )}

              {preview && !guideReady && (
                <Alert severity="info" role="status">
                  {t('detail.haynesProTime.noTime')}
                </Alert>
              )}

              {preview && guideReady && (
                <Stack spacing={1} component="section" aria-label={t('detail.haynesProTime.previewAria')}>
                  <Typography variant="subtitle2">{t('detail.haynesProTime.source')}</Typography>
                  <Typography variant="body2">
                    {nodeLabel(preview.node)}
                  </Typography>
                  <Typography variant="body2">
                    {t('detail.haynesProTime.compare', {
                      hours: formatHaynesProHours(preview.laborHours),
                      minutes: formatHaynesProMinutes(preview.presentationMinutes),
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.haynesProTime.currentTime', {
                      hours: formatHaynesProHours(currentHours),
                    })}
                  </Typography>
                  {preview.included.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('detail.haynesProTime.includedTitle')}
                      </Typography>
                      <List dense disablePadding>
                        {preview.included.map((item, index) => (
                          <ListItemText
                            key={`${item.awNumber ?? 'included'}-${index}`}
                            primary={item.description ?? item.awNumber ?? '—'}
                            secondary={t('detail.haynesProTime.notBillable')}
                          />
                        ))}
                      </List>
                    </Box>
                  )}
                  {(preview.preliminary.length > 0 || preview.followUp.length > 0) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('detail.haynesProTime.optionalTitle')}
                      </Typography>
                      <Alert severity="info" role="status" sx={{ mt: 0.5 }}>
                        {t('detail.haynesProTime.extrasUnselected')}
                      </Alert>
                      <List dense disablePadding>
                        {[...preview.preliminary, ...preview.followUp].map((item) => (
                          <ListItemText
                            key={item.awNumber}
                            primary={item.description ?? item.awNumber}
                            secondary={t('detail.haynesProTime.advisoryUnselected')}
                          />
                        ))}
                      </List>
                    </Box>
                  )}
                </Stack>
              )}

              {overlapBlocked && (
                <Alert severity="warning" role="status">
                  {t(
                    mappedElsewhere
                      ? 'detail.haynesProTime.mappedElsewhereHint'
                      : 'detail.haynesProTime.overlapHint',
                  )}
                </Alert>
              )}

              {needsManualConsent && (
                <Box>
                  <Alert severity="warning" role="status" sx={{ mb: 1 }}>
                    {t('detail.haynesProTime.replaceManualHint')}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={replaceManualOverride}
                        onChange={(event) => setReplaceManualOverride(event.target.checked)}
                        inputProps={{ 'aria-label': t('detail.haynesProTime.replaceManualAria') }}
                      />
                    }
                    label={t('detail.haynesProTime.replaceManual')}
                  />
                </Box>
              )}

              {adoptError && adoptError !== 'manual_override' && (
                <Alert severity="error" role="alert">
                  {t(errorMessageKey(adoptError))}
                </Alert>
              )}

              {adoptPending && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  role="status"
                  aria-label={t('detail.haynesProTime.adoptPending')}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t('detail.haynesProTime.adoptPending')}</Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={adoptPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleAdopt()}
          disabled={!canAdopt}
          aria-label={t('detail.haynesProTime.adoptAria')}
        >
          {t('detail.haynesProTime.adopt')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
